import {
  CHAR_ARMOR_BASE,
  CHAR_CORE,
  CHAR_CORE_TEAM_A,
  CHAR_CORE_TEAM_B,
  CHAR_EYE_GLOW,
  CHAR_FRAME,
  CHAR_FRAME_DARK,
  hash32,
  isArmorCell,
  Part,
  PART_COUNT,
  PARTS,
  SKIN_BYTES,
  skinIndex,
  type Rarity,
} from '@vm/shared';

// Character palette ---------------------------------------------------------------------

/** 63 paintable colors for skins (index 1..63). */
export const SKIN_COLORS: number[] = [
  // Skin tones
  0xffdbac, 0xf1c27d, 0xe0ac69, 0xc68642, 0x8d5524, 0x5c3a1e,
  // Hair
  0x2a1a10, 0x5a3a20, 0x9a6a3a, 0xe8c070, 0xd85a2a, 0xe8e8e8,
  // Neutrals
  0xffffff, 0xd8d8d8, 0xa8a8a8, 0x787878, 0x484848, 0x202020,
  // Reds / oranges / yellows
  0xff4a4a, 0xc02a2a, 0x7a1a1a, 0xff9a3a, 0xc86a1a, 0xffe84a, 0xc8a82a,
  // Greens
  0x9aff6a, 0x5ac83a, 0x3a8a2a, 0x2a5a2a, 0x6a7a3a, 0x4a5a2a,
  // Blues / cyans
  0x6ae8ff, 0x3aa8e8, 0x2a6ad8, 0x1a3a9a, 0x1a2a5a, 0x5a8aa8,
  // Purples / pinks
  0xd89aff, 0x9a4ad8, 0x5a2a9a, 0xff9ad8, 0xe84aa8, 0x9a2a6a,
  // Browns / tans
  0xd8b890, 0xa8845a, 0x7a5a3a, 0x4a3a2a,
  // Camo & military
  0x7a8a5a, 0x5a6a4a, 0x3a4a3a, 0xa8986a, 0x8a7a5a,
  // Metals
  0xe8e8f0, 0xb8c0c8, 0x8890a0, 0x586070, 0xe8c040, 0xc07a4a,
  // Bright extras
  0x3ae8a8, 0xe8e83a, 0xff6ab0, 0x2ad8d8,
  // Deep teal (completes the 63 paintable colors).
  0x1f7a7a,
];

export interface ArmorStyle {
  id: string;
  name: string;
  rarity: Rarity;
  price: number;
  currency: 'coins' | 'gems';
  unlockLevel: number;
  colors: [number, number, number];
  pattern: 'solid' | 'stripes' | 'camo' | 'chevron' | 'plates' | 'glow';
  coverage: 'light' | 'medium' | 'heavy';
  glow?: number;
  /** How it is obtained when not sold in the shop (price 0). Default: shop, or starter when free. */
  source?: 'battlepass' | 'event';
}

export const ARMOR_STYLES: ArmorStyle[] = [
  { id: 'standard', name: 'Standard Issue', rarity: 'common', price: 0, currency: 'coins', unlockLevel: 1, colors: [0x5a6a4a, 0x3a4a3a, 0xa8986a], pattern: 'plates', coverage: 'medium' },
  { id: 'urban', name: 'Urban Grey', rarity: 'common', price: 400, currency: 'coins', unlockLevel: 2, colors: [0x8890a0, 0x586070, 0xd8d8d8], pattern: 'camo', coverage: 'medium' },
  { id: 'desert', name: 'Desert Storm', rarity: 'common', price: 600, currency: 'coins', unlockLevel: 4, colors: [0xd8b890, 0xa8845a, 0x7a5a3a], pattern: 'camo', coverage: 'medium' },
  { id: 'arctic', name: 'Arctic', rarity: 'rare', price: 1500, currency: 'coins', unlockLevel: 6, colors: [0xf0f4f8, 0xb8c8d8, 0x5a8aa8], pattern: 'stripes', coverage: 'heavy' },
  { id: 'scout', name: 'Scout Rig', rarity: 'rare', price: 1200, currency: 'coins', unlockLevel: 5, colors: [0x3a4a3a, 0x202020, 0xe8c040], pattern: 'solid', coverage: 'light' },
  { id: 'crimson', name: 'Crimson Guard', rarity: 'rare', price: 150, currency: 'gems', unlockLevel: 1, colors: [0xc02a2a, 0x7a1a1a, 0xe8c040], pattern: 'chevron', coverage: 'heavy' },
  { id: 'cobalt', name: 'Cobalt', rarity: 'rare', price: 150, currency: 'gems', unlockLevel: 1, colors: [0x2a6ad8, 0x1a3a9a, 0xe8e8f0], pattern: 'chevron', coverage: 'heavy' },
  { id: 'jungle', name: 'Jungle Stalker', rarity: 'epic', price: 250, currency: 'gems', unlockLevel: 1, colors: [0x3a8a2a, 0x2a5a2a, 0x9aff6a], pattern: 'camo', coverage: 'medium' },
  { id: 'neon', name: 'Neon Runner', rarity: 'epic', price: 300, currency: 'gems', unlockLevel: 1, colors: [0x202020, 0x484848, 0xff6ab0], pattern: 'glow', coverage: 'medium', glow: 0xff6ab0 },
  { id: 'gold', name: 'Gilded', rarity: 'legendary', price: 600, currency: 'gems', unlockLevel: 1, colors: [0xe8c040, 0xc8a82a, 0xffffff], pattern: 'plates', coverage: 'heavy' },
  { id: 'void', name: 'Voidwalker', rarity: 'legendary', price: 650, currency: 'gems', unlockLevel: 1, colors: [0x1a1a2a, 0x5a2a9a, 0xd89aff], pattern: 'glow', coverage: 'heavy', glow: 0xb44aff },
  { id: 'toxic', name: 'Toxic Hazard', rarity: 'epic', price: 280, currency: 'gems', unlockLevel: 1, colors: [0xe8e83a, 0x202020, 0x9aff6a], pattern: 'stripes', coverage: 'heavy', glow: 0x9aff6a },
  { id: 'woodland', name: 'Woodland Recon', rarity: 'common', price: 900, currency: 'coins', unlockLevel: 7, colors: [0x55683a, 0x3a2e1f, 0x8a9a5a], pattern: 'camo', coverage: 'light' },
  { id: 'knight_plate', name: "Knight's Plate", rarity: 'rare', price: 2200, currency: 'coins', unlockLevel: 12, colors: [0xb8c0c8, 0x8890a0, 0xe8c040], pattern: 'plates', coverage: 'heavy' },
  { id: 'carbon', name: 'Carbon Ops', rarity: 'rare', price: 180, currency: 'gems', unlockLevel: 1, colors: [0x2b2d33, 0x16171b, 0x55ccff], pattern: 'chevron', coverage: 'medium' },
  { id: 'sunrise', name: 'Sunrise Stripes', rarity: 'rare', price: 160, currency: 'gems', unlockLevel: 1, colors: [0xff9a3a, 0xff5a8a, 0xffe84a], pattern: 'stripes', coverage: 'medium' },
  { id: 'bubblegum', name: 'Bubblegum', rarity: 'rare', price: 150, currency: 'gems', unlockLevel: 1, colors: [0xff8ad0, 0x7ad8ff, 0xffffff], pattern: 'plates', coverage: 'light' },
  { id: 'royal', name: 'Royal Guard', rarity: 'epic', price: 280, currency: 'gems', unlockLevel: 1, colors: [0x5a2a9a, 0x3a1a6a, 0xe8c040], pattern: 'chevron', coverage: 'heavy' },
  { id: 'frostguard', name: 'Frostguard', rarity: 'epic', price: 300, currency: 'gems', unlockLevel: 1, colors: [0xdfefff, 0x8ab8e0, 0x4ad8ff], pattern: 'glow', coverage: 'medium', glow: 0x6ae8ff },
  { id: 'circuit', name: 'Circuit Breaker', rarity: 'epic', price: 320, currency: 'gems', unlockLevel: 1, colors: [0x10302a, 0x0a1f1a, 0x3affc0], pattern: 'glow', coverage: 'medium', glow: 0x3affc0 },
  { id: 'magma', name: 'Magma Core', rarity: 'legendary', price: 600, currency: 'gems', unlockLevel: 1, colors: [0x2a1a1a, 0x4a2020, 0xff7a1a], pattern: 'glow', coverage: 'heavy', glow: 0xff7a1a },
  // Battle pass (Season 2 set: ocean).
  { id: 'reef', name: 'Reef Runner', rarity: 'common', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass', colors: [0xff9a3a, 0x1f7a7a, 0xffe0c0], pattern: 'camo', coverage: 'light' },
  { id: 'tidecaller', name: 'Tidecaller', rarity: 'rare', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass', colors: [0x2ad8d8, 0x1a6a8a, 0xf0f8ff], pattern: 'stripes', coverage: 'medium' },
  { id: 'abyssal', name: 'Abyssal Plate', rarity: 'epic', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass', colors: [0x10204a, 0x1a3a6a, 0x4af0d0], pattern: 'plates', coverage: 'heavy' },
  { id: 'leviathan', name: 'Leviathan', rarity: 'legendary', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass', colors: [0x0a1a3a, 0x1a4a7a, 0x6affea], pattern: 'glow', coverage: 'heavy', glow: 0x3affe0 },
];
export const ARMOR_BY_ID: Record<string, ArmorStyle> = Object.fromEntries(ARMOR_STYLES.map((a) => [a.id, a]));

/**
 * First character-palette index of an armor style's 4 colors. Styles 0-15 use 64..127; later
 * styles use the free block 160..254 (255 is reserved by the mesher).
 */
export function armorPaletteBase(styleIndex: number): number {
  return styleIndex < 16 ? CHAR_ARMOR_BASE + styleIndex * 4 : 160 + (styleIndex - 16) * 4;
}
if (armorPaletteBase(ARMOR_STYLES.length - 1) + 3 > 254) throw new Error('Too many armor styles for the character palette');

/** Full 256-entry character palette (0xRRGGBB) and emissive flags. */
export const CHAR_PALETTE = new Uint32Array(256);
export const CHAR_FLAGS = new Uint8Array(256);
SKIN_COLORS.forEach((c, i) => (CHAR_PALETTE[i + 1] = c));
ARMOR_STYLES.forEach((a, i) => {
  const base = armorPaletteBase(i);
  CHAR_PALETTE[base] = a.colors[0];
  CHAR_PALETTE[base + 1] = a.colors[1];
  CHAR_PALETTE[base + 2] = a.colors[2];
  CHAR_PALETTE[base + 3] = a.glow ?? a.colors[2];
  if (a.glow) CHAR_FLAGS[base + 3] = 2;
});
CHAR_PALETTE[CHAR_FRAME] = 0x3a3f48;
CHAR_PALETTE[CHAR_FRAME_DARK] = 0x24272e;
CHAR_PALETTE[CHAR_CORE] = 0x4af0ff;
CHAR_PALETTE[CHAR_CORE_TEAM_A] = 0xff5a4a;
CHAR_PALETTE[CHAR_CORE_TEAM_B] = 0x4a9aff;
CHAR_PALETTE[CHAR_EYE_GLOW] = 0xfff0a0;
CHAR_FLAGS[CHAR_CORE] = 2;
CHAR_FLAGS[CHAR_CORE_TEAM_A] = 2;
CHAR_FLAGS[CHAR_CORE_TEAM_B] = 2;
CHAR_FLAGS[CHAR_EYE_GLOW] = 2;

/** Nearest skin palette index (1..63) for an RGB color. */
export function nearestSkinColor(rgb: number): number {
  let best = 1;
  let bestD = Infinity;
  const r = (rgb >> 16) & 255;
  const g = (rgb >> 8) & 255;
  const b = rgb & 255;
  SKIN_COLORS.forEach((c, i) => {
    const d = (((c >> 16) & 255) - r) ** 2 + (((c >> 8) & 255) - g) ** 2 + ((c & 255) - b) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i + 1;
    }
  });
  return best;
}

// Skins ----------------------------------------------------------------------------------

type Painter = (part: Part, x: number, y: number, z: number, w: number, h: number, d: number) => number;

export interface SkinPreset {
  id: string;
  name: string;
  rarity: Rarity;
  price: number;
  currency: 'coins' | 'gems';
  unlockLevel: number;
  /** How it is obtained when not sold in the shop (price 0). Default: shop, or starter when free. */
  source?: 'battlepass' | 'event';
  paint: Painter;
}

const c = (rgb: number): number => nearestSkinColor(rgb);

/** Draws a face on the head front (z = 0 is the front, facing -Z). */
function face(x: number, y: number, z: number, skin: number, eye: number, mouth: number, hair: number, hairLine = 6): number | null {
  if (y >= hairLine) return hair;
  if (z !== 0) return null;
  if (y === 4 && (x === 1 || x === 2 || x === 5 || x === 6)) return x === 2 || x === 5 ? eye : c(0xffffff);
  if (y === 2 && x >= 2 && x <= 5) return mouth;
  return skin;
}

function uniformPainter(opts: {
  skin: number;
  hair: number;
  eye: number;
  shirt: number;
  shirt2?: number;
  pants: number;
  boots: number;
  belt?: number;
  gloves?: number;
  hairLine?: number;
  pattern?: 'camo' | 'stripes' | 'none';
}): Painter {
  const skin = c(opts.skin);
  const hair = c(opts.hair);
  const eye = c(opts.eye);
  const shirt = c(opts.shirt);
  const shirt2 = c(opts.shirt2 ?? opts.shirt);
  const pants = c(opts.pants);
  const boots = c(opts.boots);
  const belt = c(opts.belt ?? 0x202020);
  const gloves = opts.gloves !== undefined ? c(opts.gloves) : skin;
  const mouth = c(0x7a1a1a);
  return (part, x, y, z) => {
    const camo = opts.pattern === 'camo' && hash32(x >> 1, y >> 1, z >> 1, part) % 3 === 0;
    const stripe = opts.pattern === 'stripes' && ((y >> 1) & 1) === 0;
    const top = camo || stripe ? shirt2 : shirt;
    switch (part) {
      case Part.Head:
        return face(x, y, z, skin, eye, mouth, hair, opts.hairLine ?? 6) ?? skin;
      case Part.Torso:
        if (y <= 1) return belt;
        return top;
      case Part.ArmL:
      case Part.ArmR:
        if (y <= 2) return gloves;
        return top;
      default:
        if (y <= 2) return boots;
        return camo ? shirt2 : pants;
    }
  };
}


// Costume painter helpers ------------------------------------------------------------------

/** True on the outer side of a limb (away from the body). */
const outer = (part: Part, x: number, w: number): boolean => (part === Part.ArmL || part === Part.LegL ? x === 0 : x === w - 1);
const isArm = (part: Part): boolean => part === Part.ArmL || part === Part.ArmR;
/** Two-tone blotch camo in 2x2 cells. */
const blotch = (part: Part, x: number, y: number, z: number, a: number, b: number, cc: number, seed: number): number => {
  const h = hash32(x >> 1, y >> 1, z >> 1, part * 31 + seed) % 7;
  return h < 3 ? a : h < 6 ? b : cc;
};
/** Standard face with custom colors, or null for non-face cells. */
function faceCell(x: number, y: number, z: number, skin: number, eye: number, mouth: number): number | null {
  if (z !== 0) return null;
  if (y === 4 && (x === 1 || x === 2 || x === 5 || x === 6)) return x === 2 || x === 5 ? eye : c(0xffffff);
  if (y === 2 && x >= 2 && x <= 5) return mouth;
  return skin;
}

function ninjaPainter(suit: number, band: number, eye: number, sash: number, trim: number): Painter {
  const S = c(suit);
  const B = c(band);
  const E = c(eye);
  const H = c(sash);
  const T = c(trim);
  return (part, x, y, z, w, _h, d) => {
    if (part === Part.Head) {
      if (z === 0 && y === 4 && (x === 2 || x === 5)) return E;
      if (z === 0 && y >= 3 && y <= 5 && x >= 1 && x <= 6) return B;
      if (y === 6) return H;
      if (z === d - 1 && y >= 4 && y <= 5 && x >= 3 && x <= 4) return H;
      return S;
    }
    if (part === Part.Torso) {
      if (y === 5 || (y === 4 && z === 0 && x >= 3 && x <= 4)) return H;
      if (z === 0 && ((x === 2 && y >= 6) || (x === 5 && y >= 6)) && (x + y) % 3 === 0) return T;
      return S;
    }
    if (isArm(part)) return y === 3 || y === 0 ? H : S;
    return y <= 1 ? T : y === 4 ? H : S;
  };
}

function robotPainter(body: number, dark: number, eye: number, core: number, seed: number): Painter {
  const Bd = c(body);
  const D = c(dark);
  const E = c(eye);
  const C = c(core);
  const G = c(0x8890a0);
  return (part, x, y, z, w, h, d) => {
    const patch = hash32(x, y, z, part + seed) % 6 === 0 ? D : Bd;
    if (part === Part.Head) {
      if (z === 0 && y === 4 && x >= 2 && x <= 5) return E;
      if (z === 0 && y === 2 && x >= 2 && x <= 5) return x % 2 ? D : G;
      if (z === 0 && (x === 0 || x === 7) && (y === 1 || y === 6)) return G;
      if (y === 7 && x >= 3 && x <= 4 && z >= 3 && z <= 4) return E;
      return patch;
    }
    if (part === Part.Torso) {
      if (z === 0 && x >= 2 && x <= 5 && y >= 5 && y <= 9) return x === 2 || x === 5 || y === 5 || y === 9 ? G : C;
      if (y <= 1) return D;
      return patch;
    }
    if (isArm(part)) return y <= 2 ? G : y === 6 ? D : patch;
    return y <= 1 ? G : y === 6 ? D : patch;
  };
}

export const SKIN_PRESETS: SkinPreset[] = [
  {
    id: 'marine', name: 'Voxel Marine', rarity: 'common', price: 0, currency: 'coins', unlockLevel: 1,
    paint: uniformPainter({ skin: 0xe0ac69, hair: 0x5a3a20, eye: 0x2a6ad8, shirt: 0x5a6a4a, shirt2: 0x3a4a3a, pants: 0x4a5a2a, boots: 0x2a1a10, pattern: 'camo' }),
  },
  {
    id: 'rookie', name: 'Rookie', rarity: 'common', price: 0, currency: 'coins', unlockLevel: 1,
    paint: uniformPainter({ skin: 0xf1c27d, hair: 0xe8c070, eye: 0x3a8a2a, shirt: 0x3aa8e8, pants: 0x1a2a5a, boots: 0x484848 }),
  },
  {
    id: 'medic', name: 'Field Medic', rarity: 'common', price: 500, currency: 'coins', unlockLevel: 3,
    paint: uniformPainter({ skin: 0x8d5524, hair: 0x2a1a10, eye: 0x5a3a20, shirt: 0xffffff, shirt2: 0xff4a4a, pants: 0xd8d8d8, boots: 0x484848, pattern: 'stripes' }),
  },
  {
    id: 'engineer', name: 'Engineer', rarity: 'common', price: 800, currency: 'coins', unlockLevel: 5,
    paint: uniformPainter({ skin: 0xc68642, hair: 0x2a1a10, eye: 0x2a1a10, shirt: 0xff9a3a, shirt2: 0xe8e83a, pants: 0x1a3a9a, boots: 0x4a3a2a, gloves: 0xe8e83a, pattern: 'stripes' }),
  },
  {
    id: 'ninja', name: 'Shadow Ninja', rarity: 'rare', price: 2500, currency: 'coins', unlockLevel: 8,
    paint: (part, x, y, z) => {
      if (part === Part.Head) {
        if (z === 0 && y === 4 && (x === 2 || x === 5)) return c(0xff4a4a);
        if (z === 0 && y >= 3 && y <= 5 && x >= 1 && x <= 6) return c(0xf1c27d);
        return c(0x202020);
      }
      if (part === Part.Torso && y === 5) return c(0xc02a2a);
      return c(0x202020);
    },
  },
  {
    id: 'robot', name: 'Mech Unit', rarity: 'rare', price: 200, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z) => {
      if (part === Part.Head) {
        if (z === 0 && y === 4 && x >= 1 && x <= 6) return c(0x6ae8ff);
        return hash32(x, y, z) % 5 === 0 ? c(0x8890a0) : c(0xb8c0c8);
      }
      if (part === Part.Torso && z === 0 && y >= 6 && y <= 8 && x >= 3 && x <= 4) return c(0x6ae8ff);
      return (x + y + z) % 4 === 0 ? c(0x586070) : c(0xb8c0c8);
    },
  },
  {
    id: 'astronaut', name: 'Astronaut', rarity: 'epic', price: 350, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z) => {
      if (part === Part.Head) {
        if (z === 0 && y >= 2 && y <= 5 && x >= 1 && x <= 6) return c(0x1a2a5a);
        return c(0xe8e8f0);
      }
      if (part === Part.Torso && z === 0 && y >= 7 && y <= 9 && x >= 2 && x <= 5) return (x + y) % 2 ? c(0xff4a4a) : c(0x2a6ad8);
      return y <= 1 && part >= Part.LegL ? c(0x484848) : c(0xe8e8f0);
    },
  },
  {
    id: 'pirate', name: 'Sky Pirate', rarity: 'epic', price: 300, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w) => {
      if (part === Part.Head) {
        if (y >= 6) return c(0xc02a2a);
        if (z === 0 && y === 4 && x >= 4 && x <= 6) return c(0x202020);
        if (z === 0 && y === 4 && x === 2) return c(0x2a1a10);
        if (z === 0 && y <= 2 && x >= 1 && x <= 6) return c(0x2a1a10);
        return c(0xe0ac69);
      }
      if (part === Part.Torso) return (y >> 1) % 2 ? c(0xffffff) : c(0x1a2a5a);
      if (part >= Part.LegL) return y <= 3 ? c(0x2a1a10) : c(0x7a5a3a);
      return x === w - 1 && y <= 2 ? c(0xe0ac69) : c(0x1a2a5a);
    },
  },
  {
    id: 'zombie', name: 'Corrupted', rarity: 'common', price: 0, currency: 'coins', unlockLevel: 999,
    paint: (part, x, y, z) => {
      if (part === Part.Head) {
        if (z === 0 && y === 4 && (x === 2 || x === 5)) return c(0xe8e83a);
        if (z === 0 && y === 2 && x >= 1 && x <= 6) return c(0x202020);
        return hash32(x, y, z, 9) % 4 === 0 ? c(0x5a2a9a) : c(0x9a4ad8);
      }
      return hash32(x, y, z, part) % 3 === 0 ? c(0x5a2a9a) : part === Part.Torso ? c(0x3a8a2a) : c(0x4a5a2a);
    },
  },
  // Costumes (appended; never reorder).
  {
    id: 'knight', name: 'Iron Knight', rarity: 'epic', price: 320, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const steel = c(0xb8c0c8);
      const steelD = c(0x8890a0);
      const dark = c(0x202020);
      const blue = c(0x2a6ad8);
      const gold = c(0xe8c040);
      if (part === Part.Head) {
        if (y === 7 && x >= 3 && x <= 4) return c(0xc02a2a);
        if (z === 0 && y === 4 && x >= 1 && x <= 6) return dark;
        if (z === 0 && y <= 2 && x >= 2 && x <= 5 && (x + y) % 2 === 0) return steelD;
        return y === 5 ? steelD : steel;
      }
      if (part === Part.Torso) {
        if ((z === 0 || z === d - 1) && x >= 2 && x <= 5 && y >= 1) {
          if (((x === 3 || x === 4) && y >= 3 && y <= 9) || (y === 7 && x >= 2 && x <= 5)) return gold;
          return blue;
        }
        if (y <= 1) return c(0x5a3a20);
        return y % 4 === 0 ? steelD : steel;
      }
      if (isArm(part)) return y <= 2 ? dark : y % 4 === 3 ? steelD : steel;
      return y <= 1 ? dark : y % 4 === 1 ? steelD : steel;
    },
  },
  {
    id: 'moonwalker', name: 'Moonwalker', rarity: 'rare', price: 180, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const suit = c(0xff9a3a);
      const white = c(0xe8e8f0);
      const grey = c(0xb8c0c8);
      if (part === Part.Head) {
        if (z === 0 && y >= 2 && y <= 5 && x >= 1 && x <= 6) return x === 2 && y === 5 ? c(0xffffff) : c(0xe8c040);
        return y === 1 ? grey : white;
      }
      if (part === Part.Torso) {
        if (z === 0 && x >= 2 && x <= 5 && y >= 6 && y <= 9) return y === 8 && x === 3 ? c(0xff4a4a) : y === 8 && x === 4 ? c(0x2ad8d8) : grey;
        if (z === d - 1 && x >= 1 && x <= 6 && y >= 3 && y <= 10) return white;
        return y <= 1 ? grey : suit;
      }
      if (isArm(part)) return y <= 2 ? white : y === 6 ? white : suit;
      return y <= 2 ? white : y === 6 ? grey : suit;
    },
  },
  {
    id: 'star_cadet', name: 'Star Cadet', rarity: 'rare', price: 200, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const navy = c(0x1a2a5a);
      const red = c(0xc02a2a);
      const white = c(0xe8e8f0);
      if (part === Part.Head) {
        if (z === 0 && y >= 2 && y <= 5 && x >= 1 && x <= 6) return y === 5 && x === 5 ? white : c(0x2ad8d8);
        if (y === 7 && x === 6 && z === 3) return red;
        return white;
      }
      if (part === Part.Torso) {
        if (z === 0 && ((y === 8 && x >= 2 && x <= 5) || ((x === 3 || x === 4) && y >= 6 && y <= 9))) return c(0xffe84a);
        if (x === 0 || x === w - 1) return red;
        return y <= 1 ? white : navy;
      }
      if (isArm(part)) return y <= 2 ? white : outer(part, x, w) ? red : navy;
      return y <= 2 ? white : outer(part, x, w) ? red : navy;
    },
  },
  {
    id: 'chef', name: 'Chef Voxel', rarity: 'common', price: 1200, currency: 'coins', unlockLevel: 6,
    paint: (part, x, y, z, w, h, d) => {
      const skin = c(0xf1c27d);
      const white = c(0xffffff);
      if (part === Part.Head) {
        if (y >= 6) return white;
        if (y === 5 && (x === 0 || x === 7)) return c(0x5a3a20);
        if (z === 0 && y === 3 && x >= 2 && x <= 5) return c(0x5a3a20);
        return faceCell(x, y, z, skin, c(0x2a6ad8), c(0x7a1a1a)) ?? skin;
      }
      if (part === Part.Torso) {
        if (y >= 10 && (z === 0 || x === 0 || x === w - 1)) return c(0xc02a2a);
        if (z === 0 && (x === 2 || x === 5) && y % 2 === 1 && y >= 3 && y <= 9) return c(0x787878);
        return white;
      }
      if (isArm(part)) return y <= 2 ? skin : white;
      if (y <= 1) return c(0x202020);
      return (x + y + z) % 2 ? c(0x202020) : c(0xd8d8d8);
    },
  },
  {
    id: 'firefighter', name: 'Firefighter', rarity: 'common', price: 1500, currency: 'coins', unlockLevel: 9,
    paint: (part, x, y, z, w, h, d) => {
      const skin = c(0xc68642);
      const coat = c(0xc8a82a);
      const band = c(0xe8e8f0);
      const red = c(0xc02a2a);
      if (part === Part.Head) {
        if (y >= 6) return z === 0 && y === 6 && x >= 3 && x <= 4 ? c(0xe8c040) : red;
        if (y === 5 && z === 0) return c(0x7a1a1a);
        return faceCell(x, y, z, skin, c(0x5a3a20), c(0x7a1a1a)) ?? skin;
      }
      if (part === Part.Torso) return y === 3 || y === 7 ? band : y <= 1 ? c(0x484848) : coat;
      if (isArm(part)) return y <= 2 ? c(0x202020) : y === 6 ? band : coat;
      return y <= 2 ? c(0x202020) : y === 6 ? band : coat;
    },
  },
  {
    id: 'frost_ninja', name: 'Frost Ninja', rarity: 'epic', price: 280, currency: 'gems', unlockLevel: 1,
    paint: ninjaPainter(0xe8e8f0, 0xf1c27d, 0x2a6ad8, 0x6ae8ff, 0xb8c0c8),
  },
  {
    id: 'jade_ninja', name: 'Jade Ninja', rarity: 'rare', price: 3500, currency: 'coins', unlockLevel: 14,
    paint: ninjaPainter(0x2a5a2a, 0xe0ac69, 0x202020, 0xe8c040, 0x3a8a2a),
  },
  {
    id: 'rusty_bot', name: 'Rusty Bot', rarity: 'common', price: 2000, currency: 'coins', unlockLevel: 11,
    paint: robotPainter(0xc07a4a, 0x7a5a3a, 0xffe84a, 0xe8e83a, 3),
  },
  {
    id: 'golden_mech', name: 'Golden Mech', rarity: 'legendary', price: 600, currency: 'gems', unlockLevel: 1,
    paint: robotPainter(0xe8c040, 0x484848, 0xff4a4a, 0xff4a4a, 7),
  },
  {
    id: 'trail_rider', name: 'Trail Rider', rarity: 'rare', price: 220, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const skin = c(0xe0ac69);
      const hat = c(0x7a5a3a);
      const vest = c(0x4a3a2a);
      if (part === Part.Head) {
        if (y >= 6) return y === 6 ? c(0x202020) : hat;
        if (y === 5 && (z === 0 || z === d - 1 || x === 0 || x === 7)) return hat;
        return faceCell(x, y, z, skin, c(0x5a3a20), c(0x7a1a1a)) ?? skin;
      }
      if (part === Part.Torso) {
        if (z === 0 && y >= 9 && x >= 2 && x <= 5 && (y >= 10 || x === 3 || x === 4)) return c(0xc02a2a);
        if (y <= 1) return z === 0 && y === 1 && (x === 3 || x === 4) ? c(0xe8c040) : c(0x4a3a2a);
        if (z === 0 && x >= 3 && x <= 4) return c(0xffffff);
        return vest;
      }
      if (isArm(part)) return y <= 2 ? skin : y === 3 ? vest : c(0xffffff);
      if (y <= 2) return y === 0 && z === d - 1 ? c(0xe8c040) : c(0x5a3a20);
      return (y + x) % 5 === 0 ? c(0x1a3a9a) : c(0x2a6ad8);
    },
  },
  {
    id: 'lab_genius', name: 'Lab Genius', rarity: 'rare', price: 3000, currency: 'coins', unlockLevel: 13,
    paint: (part, x, y, z, w, h, d) => {
      const skin = c(0xffdbac);
      const coat = c(0xffffff);
      if (part === Part.Head) {
        if (y === 4) return z === 0 && (x === 1 || x === 2 || x === 5 || x === 6) ? c(0x6ae8ff) : c(0x484848);
        if (y >= 6 || (y === 5 && (x === 0 || x === 7) && hash32(x, y, z) % 2 === 0)) return hash32(x, y, z, 5) % 3 ? c(0xe8e8e8) : c(0xa8a8a8);
        return faceCell(x, y, z, skin, c(0x2a6ad8), c(0x7a1a1a)) ?? skin;
      }
      if (part === Part.Torso) {
        if (z === 0 && x >= 3 && x <= 4) return y >= 4 && y <= 10 && x === 3 ? c(0x9a4ad8) : c(0x5a2a9a);
        if (z === 0 && x === 1 && y === 8) return c(0x2a6ad8);
        if (z === 0 && x === 2 && y === 8) return c(0xff4a4a);
        return coat;
      }
      if (isArm(part)) return y <= 2 ? c(0x9aff6a) : coat;
      return y <= 1 ? c(0x202020) : c(0x787878);
    },
  },
  {
    id: 'deep_diver', name: 'Deep Diver', rarity: 'epic', price: 300, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const brass = c(0xc8a82a);
      const navy = c(0x1a2a5a);
      if (part === Part.Head) {
        if (z === 0) {
          const r = (x - 3.5) ** 2 + (y - 3.5) ** 2;
          if (r <= 5.5) return x === 2 && y === 5 ? c(0x6ae8ff) : c(0x1a3a9a);
          if (r <= 9) return c(0xb8c0c8);
        }
        if ((x === 0 || x === 7) && y >= 3 && y <= 4 && z >= 3 && z <= 4) return c(0x1a3a9a);
        return (y + z) % 5 === 0 ? c(0xe8c040) : brass;
      }
      if (part === Part.Torso) {
        if (y >= 11) return brass;
        if (y <= 1) return c(0xe8e83a);
        if (z === d - 1 && x >= 2 && x <= 5 && y >= 3 && y <= 10) return c(0x8890a0);
        return navy;
      }
      if (isArm(part)) return y <= 2 ? c(0x202020) : navy;
      return y <= 2 ? (y === 2 ? brass : c(0x484848)) : navy;
    },
  },
  {
    id: 'captain_voxel', name: 'Captain Voxel', rarity: 'legendary', price: 550, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const skin = c(0xe0ac69);
      const blue = c(0x2a6ad8);
      const red = c(0xc02a2a);
      const gold = c(0xffe84a);
      if (part === Part.Head) {
        if (y >= 6) return c(0x2a1a10);
        if (z === 0 && (y === 4 || y === 5) && x >= 0 && x <= 7) {
          if (y === 4 && (x === 1 || x === 6)) return c(0xffffff);
          if (y === 4 && (x === 2 || x === 5)) return c(0x2a6ad8);
          return c(0x1a3a9a);
        }
        return faceCell(x, y, z, skin, c(0x2a6ad8), c(0x7a1a1a)) ?? skin;
      }
      if (part === Part.Torso) {
        if (z === d - 1 || ((x === 0 || x === w - 1) && z >= 2)) return red;
        if (y <= 1) return gold;
        if (z === 0) {
          const bolt = (y === 9 && (x === 4 || x === 5)) || (y === 8 && (x === 3 || x === 4)) || (y === 7 && x >= 3 && x <= 5) || (y === 6 && x === 4) || (y === 5 && x === 3);
          if (bolt) return gold;
        }
        return blue;
      }
      if (isArm(part)) return y <= 2 ? red : blue;
      return y <= 3 ? red : blue;
    },
  },
  {
    id: 'dino', name: 'Dino Hoodie', rarity: 'epic', price: 350, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const green = c(0x5ac83a);
      const dark = c(0x3a8a2a);
      const spike = c(0x9aff6a);
      const skin = c(0xf1c27d);
      if (part === Part.Head) {
        if (z === 0 && y >= 1 && y <= 5 && x >= 1 && x <= 6) return faceCell(x, y, z, skin, c(0x2a1a10), c(0x7a1a1a)) ?? skin;
        if (z === 0 && y === 6 && x >= 1 && x <= 6) return x % 2 ? c(0xffffff) : green;
        if (y === 7 && x >= 3 && x <= 4 && z % 2 === 1) return spike;
        if (y === 7 && (x === 1 || x === 6) && z === 1) return c(0xffffff);
        return green;
      }
      if (part === Part.Torso) {
        if (z === 0 && x >= 2 && x <= 5 && y >= 1 && y <= 10) return c(0xd8b890);
        if (z === d - 1 && x >= 3 && x <= 4 && y % 2 === 0) return spike;
        return green;
      }
      if (isArm(part)) return y === 0 && z === 0 ? c(0xffffff) : y <= 2 ? dark : green;
      return y === 0 && z === 0 ? c(0xffffff) : y <= 1 ? dark : green;
    },
  },
  {
    id: 'pumpkin_head', name: 'Pumpkin Head', rarity: 'epic', price: 300, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const orange = c(0xff9a3a);
      const rib = c(0xc86a1a);
      const glow = c(0xffe84a);
      if (part === Part.Head) {
        if (y === 7 && x >= 3 && x <= 4 && z >= 3 && z <= 4) return c(0x3a8a2a);
        if (z === 0) {
          const eye = (y === 4 && (x === 1 || x === 2 || x === 5 || x === 6)) || (y === 5 && (x === 2 || x === 5));
          const nose = y === 3 && (x === 3 || x === 4);
          const mouth = (y === 2 && (x === 1 || x === 3 || x === 4 || x === 6)) || (y === 1 && x >= 2 && x <= 5);
          if (eye || nose || mouth) return glow;
        }
        const col = z === 0 || z === d - 1 ? x : z;
        return col % 2 ? rib : orange;
      }
      if (part === Part.Torso) {
        if (y <= 1) return orange;
        if (z === 0 && (x === 3 || x === 4)) return c(0x202020);
        return c(0x5a2a9a);
      }
      if (isArm(part)) return y <= 2 ? c(0x3a8a2a) : y === 3 ? c(0x5a2a9a) : c(0x202020);
      return y <= 1 ? c(0x4a3a2a) : c(0x202020);
    },
  },
  {
    id: 'snowman', name: 'Snow Buddy', rarity: 'epic', price: 300, currency: 'gems', unlockLevel: 1,
    paint: (part, x, y, z, w, h, d) => {
      const snow = c(0xffffff);
      const coal = c(0x202020);
      const red = c(0xc02a2a);
      if (part === Part.Head) {
        if (y >= 6) return y === 6 ? red : coal;
        if (z === 0) {
          if (y === 4 && (x === 2 || x === 5)) return coal;
          if (y === 3 && (x === 3 || x === 4)) return c(0xff9a3a);
          if ((y === 2 && (x === 1 || x === 6)) || (y === 1 && x >= 2 && x <= 5)) return coal;
        }
        return snow;
      }
      if (part === Part.Torso) {
        if (y >= 10) return red;
        if (z === 0 && x === 5 && y >= 5 && y <= 9) return red;
        if (z === 0 && (x === 3 || x === 4) && (y === 3 || y === 6 || y === 9)) return coal;
        return snow;
      }
      if (isArm(part)) return y <= 2 ? red : c(0x7a5a3a);
      return y <= 1 ? c(0xd8d8d8) : snow;
    },
  },
  {
    id: 'arctic_trooper', name: 'Arctic Trooper', rarity: 'common', price: 1800, currency: 'coins', unlockLevel: 10,
    paint: (part, x, y, z, w, h, d) => {
      const skin = c(0xffdbac);
      const white = c(0xe8e8f0);
      const grey = c(0xb8c0c8);
      const fur = (x + y) % 2 ? c(0xffffff) : c(0xd8d8d8);
      if (part === Part.Head) {
        if (y === 4) return z === 0 && (x === 1 || x === 2 || x === 5 || x === 6) ? c(0xff9a3a) : c(0x484848);
        if (z === 0 && (y === 6 || x === 0 || x === 7)) return fur;
        if (y >= 6) return white;
        return faceCell(x, y, z, skin, c(0x2a6ad8), c(0x7a1a1a)) ?? skin;
      }
      if (part === Part.Torso) return y <= 1 ? c(0x484848) : blotch(part, x, y, z, white, grey, c(0xa8a8a8), 11);
      if (isArm(part)) return y <= 2 ? c(0x484848) : blotch(part, x, y, z, white, grey, c(0xa8a8a8), 12);
      return y <= 2 ? c(0x787878) : blotch(part, x, y, z, white, grey, c(0xa8a8a8), 13);
    },
  },
  {
    id: 'desert_trooper', name: 'Desert Trooper', rarity: 'common', price: 1000, currency: 'coins', unlockLevel: 7,
    paint: (part, x, y, z, w, h, d) => {
      const tan = c(0xd8b890);
      const tan2 = c(0xa8845a);
      const brown = c(0x7a5a3a);
      if (part === Part.Head) {
        if (y === 4 && z === 0) return x === 1 || x === 2 || x === 5 || x === 6 ? c(0xc8a82a) : c(0x484848);
        if (y === 4) return c(0x484848);
        return (x + y + z) % 3 === 0 ? c(0xa8986a) : tan;
      }
      if (part === Part.Torso) {
        if (z === 0 && y >= 3 && y <= 6 && (x <= 2 || x >= 5) && x >= 1 && x <= 6) return brown;
        return y <= 1 ? brown : blotch(part, x, y, z, tan, tan2, c(0x8a7a5a), 21);
      }
      if (isArm(part)) return y <= 2 ? brown : blotch(part, x, y, z, tan, tan2, c(0x8a7a5a), 22);
      return y <= 2 ? brown : blotch(part, x, y, z, tan, tan2, c(0x8a7a5a), 23);
    },
  },
  // Battle pass (Season 2 set: ocean).
  {
    id: 'diver', name: 'Deep Diver', rarity: 'rare', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass',
    paint: (part, x, y, z, w, h, d) => {
      const brass = c(0xc8a82a);
      const suit = c(0x1a3a9a);
      const yellow = c(0xe8e83a);
      if (part === Part.Head) {
        const visor = z === 0 && y >= 2 && y <= 5 && x >= 1 && x <= 6 && !((x === 1 || x === 6) && (y === 2 || y === 5));
        if (visor) return y === 5 || (y === 4 && x === 2) ? c(0x2ad8d8) : c(0x1f7a7a);
        if (y === 1 && (x + z) % 3 === 0) return c(0x8a7a5a);
        return brass;
      }
      if (part === Part.Torso) {
        if (y <= 1) return c(0x202020);
        if (z === d - 1 && x >= 2 && x <= 5 && y >= 3 && y <= 9) return c(0xe8c040);
        if (y === 7 || x === 0 || x === w - 1) return yellow;
        return suit;
      }
      if (isArm(part)) return y <= 2 ? yellow : y === 6 ? yellow : suit;
      return y <= 2 ? c(0x202020) : y === 5 ? yellow : suit;
    },
  },
  {
    id: 'coral_ranger', name: 'Coral Ranger', rarity: 'epic', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass',
    paint: (part, x, y, z, w, h, d) => {
      const skin = c(0xc68642);
      const teal = c(0x1f7a7a);
      if (part === Part.Head) {
        if (y === 6) return c(0x2ad8d8);
        return face(x, y, z, skin, c(0x2a1a10), c(0x7a1a1a), c(0x2a1a10), 7) ?? skin;
      }
      if (part === Part.Torso) return y <= 1 ? c(0x4a3a2a) : blotch(part, x, y, z, c(0xff9a3a), c(0xff6ab0), c(0x2ad8d8), 31);
      if (isArm(part)) return y <= 2 ? c(0x2ad8d8) : blotch(part, x, y, z, c(0xff9a3a), c(0xff6ab0), c(0x2ad8d8), 32);
      return y <= 2 ? c(0x4a3a2a) : teal;
    },
  },
  {
    id: 'shark_suit', name: 'Shark Suit', rarity: 'legendary', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass',
    paint: (part, x, y, z, w, h, d) => {
      const grey = c(0x586070);
      const fin = c(0x484848);
      const belly = c(0xe8e8f0);
      if (part === Part.Head) {
        if (z === 0 && y >= 2 && y <= 5 && x >= 1 && x <= 6) return faceCell(x, y, z, c(0xf1c27d), c(0x2a1a10), c(0x7a1a1a)) ?? grey;
        if ((x === 0 || x === w - 1) && y === 5 && z === 2) return c(0x202020);
        if (y === h - 1 && x >= 3 && x <= 4 && z >= 2 && z <= 5) return fin;
        return grey;
      }
      if (part === Part.Torso) {
        if (z === 0 && x >= 2 && x <= 5 && y >= 2 && y <= 9) return belly;
        if (z === d - 1 && x >= 3 && x <= 4 && y >= 6) return fin;
        if ((x === 0 || x === w - 1) && y >= 7 && y <= 9 && z % 2 === 1) return fin;
        return grey;
      }
      if (isArm(part)) return y <= 2 ? belly : outer(part, x, w) ? fin : grey;
      return y <= 1 ? fin : grey;
    },
  },
];
export const SKIN_PRESET_BY_ID: Record<string, SkinPreset> = Object.fromEntries(SKIN_PRESETS.map((s) => [s.id, s]));

export function paintSkin(p: Painter): Uint8Array {
  const out = new Uint8Array(SKIN_BYTES);
  for (let part = 0 as Part; part < PART_COUNT; part = (part + 1) as Part) {
    const [w, h, d] = PARTS[part].bodySize;
    for (let y = 0; y < h; y++) for (let z = 0; z < d; z++) for (let x = 0; x < w; x++) out[skinIndex(part, x, y, z)] = p(part, x, y, z, w, h, d);
  }
  return out;
}

const presetCache = new Map<string, Uint8Array>();
export function presetSkin(id: string): Uint8Array {
  let s = presetCache.get(id);
  if (!s) {
    s = paintSkin((SKIN_PRESET_BY_ID[id] ?? SKIN_PRESETS[0]).paint);
    presetCache.set(id, s);
  }
  return s;
}

export function encodeSkin(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

export function decodeSkin(str: string): Uint8Array {
  if (str.startsWith('preset:')) return presetSkin(str.slice(7));
  try {
    const bin = atob(str);
    if (bin.length !== SKIN_BYTES) return presetSkin('marine');
    const out = new Uint8Array(SKIN_BYTES);
    for (let i = 0; i < bin.length; i++) out[i] = Math.min(63, bin.charCodeAt(i));
    return out;
  } catch {
    return presetSkin('marine');
  }
}

// Model assembly -------------------------------------------------------------------------

export const VoxelKind = { Empty: 0, Body: 1, Armor: 2 } as const;

export interface PartGrid {
  part: Part;
  sx: number;
  sy: number;
  sz: number;
  /** Character palette index per cell (layout x + z*sx + y*sx*sz). */
  color: Uint8Array;
  kind: Uint8Array;
  /** Depth below the body surface (0 = surface). Armor cells are 0. */
  depth: Uint8Array;
}

function armorCovers(style: ArmorStyle, part: Part, x: number, y: number, z: number): boolean {
  // x, y, z in body voxel coords. Helmets always leave a face window (brow to chin at the front)
  // so skins stay recognizable under armor.
  if (part === Part.Head && style.coverage !== 'light' && z === -5 && y <= 29) return false;
  if (style.coverage === 'heavy') return true;
  if (style.coverage === 'medium') {
    if (part === Part.ArmL || part === Part.ArmR) return y >= 20; // shoulder pads
    if (part === Part.LegL || part === Part.LegR) return y >= 4 && y <= 9 && z === -3; // knee guards
    if (part === Part.Torso) return z === 2 || y >= 17; // vest: back plate + upper chest
    return true;
  }
  // light: helmet top + chest front
  if (part === Part.Head) return y >= 30;
  if (part === Part.Torso) return z === -3 && y >= 15;
  return false;
}

function armorColor(style: ArmorStyle, styleIndex: number, x: number, y: number, z: number): number {
  const base = armorPaletteBase(styleIndex);
  switch (style.pattern) {
    case 'solid':
      return base;
    case 'stripes':
      return (y >> 1) % 3 === 0 ? base + 1 : base;
    case 'camo':
      return hash32(x >> 1, y >> 1, z >> 1, 77) % 3 === 0 ? base + 1 : hash32(x, y, z, 5) % 11 === 0 ? base + 2 : base;
    case 'chevron':
      return (Math.abs(x) + y) % 6 === 0 ? base + 2 : base;
    case 'plates':
      return y % 4 === 0 ? base + 1 : base;
    case 'glow':
      return (y % 5 === 0 || Math.abs(x) === 4) ? base + 3 : hash32(x, y, z) % 4 === 0 ? base + 1 : base;
  }
}

/**
 * Builds the six voxel part grids for a character: painted skin surface, a dark frame
 * underneath, a glowing core in the chest, and the armor shell on top.
 * team: 0/1 tints the core; -1 uses the neutral cyan core.
 */
export function buildCharacter(skin: Uint8Array, armorId: string, team: number): PartGrid[] {
  const styleIndex = Math.max(0, ARMOR_STYLES.findIndex((a) => a.id === armorId));
  const style = ARMOR_STYLES[styleIndex];
  const core = team === 0 ? CHAR_CORE_TEAM_A : team === 1 ? CHAR_CORE_TEAM_B : CHAR_CORE;
  const grids: PartGrid[] = [];
  for (const def of PARTS) {
    const [sx, sy, sz] = def.size;
    const n = sx * sy * sz;
    const g: PartGrid = { part: def.id, sx, sy, sz, color: new Uint8Array(n), kind: new Uint8Array(n), depth: new Uint8Array(n) };
    const [bw, bh, bd] = def.bodySize;
    for (let gy = 0; gy < sy; gy++)
      for (let gz = 0; gz < sz; gz++)
        for (let gx = 0; gx < sx; gx++) {
          const i = gx + gz * sx + gy * sx * sz;
          const x = gx + def.min[0];
          const y = gy + def.min[1];
          const z = gz + def.min[2];
          const bx = x - def.bodyMin[0];
          const by = y - def.bodyMin[1];
          const bz = z - def.bodyMin[2];
          if (bx >= 0 && by >= 0 && bz >= 0 && bx < bw && by < bh && bz < bd) {
            const depth = Math.min(bx, by, bz, bw - 1 - bx, bh - 1 - by, bd - 1 - bz);
            g.kind[i] = VoxelKind.Body;
            g.depth[i] = depth;
            if (depth === 0) g.color[i] = Math.max(1, skin[skinIndex(def.id, bx, by, bz)]);
            else if (def.id === Part.Torso && by >= 6 && by <= 9 && bx >= 3 && bx <= 4) g.color[i] = core;
            else if (def.id === Part.Head && depth === 1 && bz === 1 && by === 4) g.color[i] = CHAR_EYE_GLOW;
            else g.color[i] = depth === 1 ? CHAR_FRAME : CHAR_FRAME_DARK;
          } else if (isArmorCell(def.id, gx, gy, gz) && armorCovers(style, def.id, x, y, z)) {
            g.kind[i] = VoxelKind.Armor;
            g.color[i] = armorColor(style, styleIndex, x, y, z);
          }
        }
    grids.push(g);
  }
  return grids;
}
